<template>
    <div>
        <v-card class="soli-round">
            <v-container>
                <v-card-title class="soli-grey"
                    >Pay Rates<v-icon v-if="missingRates" right color="error"
                        >error_outline</v-icon
                    >
                    <v-spacer />
                    <v-btn icon @click="showHelp = true"
                        ><v-icon color="primary">help</v-icon></v-btn
                    ></v-card-title
                >
                <v-card-subtitle>
                    <v-btn
                        @click="
                            [
                                (showForm = true),
                                (form.time_clock_rate = getDefaultType()),
                                (form.role_name = getDefaultRole()),
                            ]
                        "
                        x-small
                        outlined
                        class="soli-round soli-blue-text"
                        :disabled="
                            !user.hasPermissions.includes('Work') &&
                            !user.hasRoles.includes('Staff')
                        "
                        >New Pay Rate</v-btn
                    >
                    <v-btn
                        @click="showPayHistory = !showPayHistory"
                        v-if="payRates.length !== currentRates.length"
                        x-small
                        outlined
                        class="soli-round soli-blue-text"
                        >{{
                            showPayHistory
                                ? "View Current Rates"
                                : "View Rate History"
                        }}</v-btn
                    ></v-card-subtitle
                >
                <v-data-table
                    :headers="rateHeaders"
                    :items="showPayHistory ? payRates : currentRates"
                    hide-default-footer
                    mobile-breakpoint="0"
                    @click:row="editPayRate($event)"
                    no-data-text="No pay rates available"
                    class="cursor-pointer table-nowrap"
                >
                    <template v-slot:[`item.amount`]="{ item }">
                        {{ "$" + item.amount + "/hour" }}
                    </template>
                    <template v-slot:[`item.status`]="{ item }">
                        <v-chip
                            pill
                            :class="
                                item.status == 'Archived'
                                    ? 'soli-grey'
                                    : item.status == 'Scheduled'
                                    ? 'soli-status-blue'
                                    : item.status == 'Active'
                                    ? 'soli-status-green'
                                    : 'soli-status-red'
                            "
                            class="white--text"
                            small
                        >
                            {{ item.status }}</v-chip
                        ></template
                    >
                    <!--<template v-slot:item.actions="{ item }">
                        <div class="flex">
                            <v-icon
                                small
                                class="mr-2"
                                @click="editPayRate(item)"
                            >
                                edit
                            </v-icon>
                            <v-icon small @click="deletePayRate(item)">
                                delete
                            </v-icon>
                        </div>
                    </template>-->
                </v-data-table>
                <SoliForm
                    v-if="showForm"
                    :title="`${form.id ? 'Edit' : 'Add'} Pay Rate`"
                    :textSubtitle="
                        disabled
                            ? 'This rate has been applied to payroll or archived and cannot be edited. Please create a new pay rate.'
                            : null
                    "
                    @reset="reset()"
                    @hook:mounted="getApiGroupEvents()"
                    @submit="submit()"
                    text="Save"
                    icon="save"
                    ref="form"
                    mobileScroll
                    :saveChanges="editRateForm"
                    persistent
                    :disabled="!!disabled"
                >
                    <template #body>
                        <div class="flex flex-col gap-4 mb-6">
                            <v-select
                                v-if="requiresRateType"
                                class="mt-0"
                                v-model="form.time_clock_rate"
                                @change="
                                    form.role_name =
                                        $event && roleOptions.length > 1
                                            ? null
                                            : getDefaultRole()
                                "
                                label="Rate Type"
                                :items="[
                                    { text: 'Teaching', value: false },
                                    { text: 'Time Clock', value: true },
                                ]"
                                outlined
                                dense
                                prepend-inner-icon="assignment"
                                hide-details="auto"
                                :rules="[
                                    (v) =>
                                        v !== null || 'Rate type is required',
                                ]"
                            />
                            <v-select
                                v-if="
                                    form.time_clock_rate &&
                                    roleOptions.length > 1
                                "
                                class="mt-0"
                                v-model="form.role_name"
                                label="Rate Role"
                                :items="roleOptions"
                                outlined
                                dense
                                prepend-inner-icon="engineering"
                                hide-details="auto"
                                :rules="rules.select"
                            />
                            <StaffPayRate
                                label="Amount per hour"
                                v-model="form.amount"
                                hide-details="auto"
                            />
                            <v-select
                                v-if="!form.time_clock_rate"
                                label="Event"
                                outlined
                                dense
                                :items="rateOptions"
                                item-text="title"
                                item-value="id"
                                v-model="form.group_event_id"
                                prepend-inner-icon="event"
                                :rules="rules.select"
                                hide-details="auto"
                            ></v-select>
                            <v-select
                                v-model="form.contract_type"
                                :items="['W2', '1099']"
                                outlined
                                class="soli-round"
                                prepend-inner-icon="gavel"
                                dense
                                label="Contract Type"
                                :rules="rules.select"
                                hide-details="auto"
                            />
                            <SoliDate
                                label="Effective Date"
                                v-model="form.starts_at"
                                :error="errors.starts_at"
                                :rules="rules.input"
                                :min="minStartDate"
                                noLimit
                                hide-details="auto"
                            ></SoliDate>
                        </div>
                    </template>
                    <template v-slot:cancelBtn="attrs">
                        <SoliBtn
                            v-if="!editRateForm && form.id"
                            text="Delete"
                            icon="delete"
                            v-bind="attrs"
                            color="soli-status-red"
                            class="mt-3"
                            :disabled="disabled"
                            @click="deletePayRate(form.id)"
                        ></SoliBtn>
                    </template>
                </SoliForm>
            </v-container>
        </v-card>
        <SoliResponsiveDialog
            v-if="showHelp"
            title="About Pay Rates"
            @reset="closeModal(() => (showHelp = false))"
            ><template #body>
                <v-card-text class="pt-0">
                    A staff member or teacher can have multiple pay rates based
                    on their roles and type of work. Time clock rates will be
                    used for any non-teaching hours, while teacher rates will be
                    applied to lessons. A pay rate is required for each role.
                    <br /><br />Pay rates can be scheduled to take effect at a
                    future date. If a rate is scheduled, it will automatically
                    become active on the specified date. Pay rates can also be
                    set retroactively, so long as a prior pay rate hasn't been
                    used to process payroll. Any pay rate that has been used to
                    process payroll is not able to be edited or removed.
                </v-card-text>
            </template>
        </SoliResponsiveDialog>
        <!--<SoliChoice
            v-if="confirmScheduleRate"
            title="Are you sure?"
            :subtitle="`This date is in the future, would you like to schedule this rate change?`"
            button1="Schedule It!"
            button2="Edit Existing Rate"
            @option1="saveRates((schedule = true))"
            @option2="saveRates((schedule = false))"
            @reset="closeModal(() => (confirmScheduleRate = false))"
        ></SoliChoice>-->
    </div>
</template>

<script>
import StaffPayRate from "./StaffPayRate.vue";

export default {
    components: {
        StaffPayRate,
    },
    props: {
        user: {
            type: Object,
            required: true,
        },
        payRates: {
            type: Array,
            required: true,
        },
        rules: {
            type: Object,
            required: true,
        },
        errors: {
            type: Object,
            default: () => {},
        },
        isTeacher: {
            type: Boolean,
            required: true,
        },
        isStaff: {
            type: Boolean,
            required: true,
        },
    },
    data() {
        return {
            rateHeaders: [
                { text: "Amount", value: "amount", align: "start" },
                {
                    text: "Event",
                    value: "title",
                    align: "start",
                },
                { text: "Status", value: "status", align: "start" },
                {
                    text: "Role",
                    value: "role.name",
                    align: "start",
                },
                {
                    text: "Contract",
                    value: "contract_type",
                    align: "start",
                    width: "98px",
                },
                { text: "Start", value: "starts_at", align: "start" },
                /*{
                    text: "Actions",
                    value: "actions",
                    align: "start",
                    sortable: false,
                },*/
            ],
            form: this.$inertia.form({
                id: null,
                location_user_id: null,
                amount: null,
                role_name: null,
                group_event_id: null,
                time_clock_rate: null,
                contract_type: null,
                starts_at: null,
            }),
            showPayHistory: false,
            showForm: false,
            formCopy: null,
            apiGroupEvents: [],
            editRateForm: false,
            minStartDate: null,
            showHelp: false,
            disabled: false,
            //confirmScheduleRate: false,
        };
    },
    computed: {
        /*editRateForm() {
            return this.form.isDirty;
        },*/
        roleOptions() {
            return [
                "Teacher",
                "Staff",
                "Assistant Community Manager",
                "Community Manager",
                "Area Manager",
            ].filter((e) => this.user.hasRoles.includes(e));
        },
        getLocationUser() {
            return this.user.locations.find(
                (e) => e.id == this.$page.props.auth.user.selected_location.id
            ).pivot.id;
        },
        requiresRateType() {
            return this.isTeacher;
        },
        rateOptions() {
            return [
                { title: "Default Teaching Rate", id: -1 },
                ...this.apiGroupEvents,
            ];
        },
        currentRates() {
            return this.payRates.filter(
                (e) => e.status == "Active" || e.status == "Scheduled"
            );
        },
        missingRates() {
            if (
                this.isTeacher &&
                !this.currentRates.find(
                    (e) =>
                        e.role.name == "Teacher" && e.time_clock_rate == false
                )
            ) {
                return true;
            }
            if (
                this.isStaff &&
                !this.currentRates.find((e) => e.role.name !== "Teacher")
            ) {
                return true;
            }
            return false;
        },
    },
    methods: {
        getDefaultType() {
            if (this.requiresRateType) return null;
            if (this.isStaff) return true;
            if (this.isTeacher) return false;
        },
        getDefaultRole() {
            if (!this.form.time_clock_rate) return "Teacher";
            return this.roleOptions[this.roleOptions.length - 1] ?? null;
        },
        submit() {
            if (!this.$refs.form.$refs.form.validate()) return;

            this.form.transform((form) => ({
                ...form,
                location_user_id: this.getLocationUser,
                group_event_id:
                    form.group_event_id == -1 ? null : form.group_event_id,
            }));

            const options = {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.$emit("success");
                    this.reset();
                },
                onError: (errors) => {
                    console.log(
                        "Form returned errors: " + JSON.stringify(errors)
                    );
                    this.$refs.form.loading = false;
                    this.$emit("reset");
                },
            };

            this.form.id
                ? this.form.put(`/pay-rates/${this.form.id}`, options)
                : this.form.post(`/pay-rates`, options);
        },
        editPayRate(rate) {
            console.log(rate.id);
            this.showForm = true;

            this.form.id = rate.id;
            this.form.location_user_id = rate.location_user_id;
            this.form.amount = rate.amount;
            this.form.contract_type = rate.contract_type;
            this.form.group_event_id =
                rate.group_event_id ??
                this.rateOptions?.find((e) => e.title == rate.title)?.id ??
                null;
            this.form.time_clock_rate = rate.time_clock_rate;
            this.form.role_name = rate.role.name;
            this.form.starts_at = this.moment(new Date(rate.starts_at))
                .toISOString()
                .substr(0, 10);

            this.minStartDate = rate.previous_rate
                ? this.moment(new Date(rate.previous_rate.ends_at))
                      .toISOString()
                      .substr(0, 10)
                : null;

            this.disabled = !rate.editable;

            this.formCopy = JSON.stringify(this.form.data());
        },
        deletePayRate(rateId) {
            this.$inertia.delete(`/pay-rates/${rateId}`, {
                preserveState: true,
                preserveScroll: true,
                onSuccess: () => {
                    this.$emit("success");
                    this.reset();
                },
                onError: (errors) => {
                    console.log(
                        "Form returned errors: " + JSON.stringify(errors)
                    );
                    this.$emit("reset");
                },
            });
        },
        reset() {
            this.$refs.form?.reset();
            setTimeout(() => {
                this.form.id = null;
                this.form.location_user_id = null;
                this.form.amount = null;
                this.form.group_event_id = null;
                this.form.contract_type = null;
                this.form.starts_at = null;
                this.form.time_clock_rate = this.getDefaultType();
                this.form.role_name = this.getDefaultRole();
                this.formCopy = JSON.stringify(this.form.data());
                this.minStartDate = null;
                this.disabled = false;
                //this.confirmScheduleRate = false;
                this.showForm = false;
            }, 300);
        },
        getApiGroupEvents() {
            this.loading = true;
            axios
                .get("/api/lessons/all-group-events")
                .then((resp) => {
                    this.apiGroupEvents = resp.data;
                    this.loading = false;
                })
                .catch((error) => {
                    this.axiosError = error;
                });
        },
    },
    watch: {
        form: {
            handler: function (val) {
                if (JSON.stringify(val.data()) == this.formCopy) {
                    this.editRateForm = false;
                } else {
                    this.editRateForm = true;
                }
            },
            deep: true,
        },
    },
};
</script>
